<template>
  <div>
    <nav class="app-topbar" role="navigation" aria-label="main navigation">
      <div class="app-topbar__brand-row">
        <div class="app-topbar__brand">
          <router-link to="/" class="app-topbar__brand-link">
            <img
              v-if="branding.clientLogoUrl"
              class="app-topbar__brand-logo app-topbar__brand-logo--client"
              :src="branding.clientLogoUrl"
              :alt="branding.clientName || 'Client logo'"
            >
            <img
              v-else
              class="app-topbar__brand-logo"
              src="@/assets/brandLogo.png"
              alt="ODIN logo"
              width="112"
              height="28"
            >
          </router-link>
          <div class="app-topbar__brand-copy is-hidden-touch">
            <p class="app-topbar__eyebrow">{{ branding.poweredByLabel }}</p>
            <p class="app-topbar__title">{{ branding.clientName || branding.productName }}</p>
            <p class="app-topbar__subtitle">{{ branding.clientTagline }}</p>
          </div>
        </div>

        <button
          id="burgerButton"
          class="app-topbar__burger navbar-burger"
          :class="{ 'is-active': isBurgerActive }"
          aria-label="menu"
          :aria-expanded="isBurgerActive ? 'true' : 'false'"
          data-target="navbarBasicExample"
          @click.prevent="burgerClick()"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>

        <div class="app-topbar__actions app-topbar__actions--desktop is-hidden-touch">
          <div class="app-topbar__bell" :class="{ 'is-active': isBellMenuOpen }">
            <a class="app-topbar__bell-trigger" href="#" @click.prevent="toggleBellMenu">
              <span class="app-topbar__bell-icon">
                <i class="fas fa-bell"></i>
              </span>
              <span v-if="totalBellCount" class="app-topbar__bell-count">{{ totalBellCount }}</span>
            </a>
            <div v-if="isBellMenuOpen" class="app-topbar__bell-dropdown">
              <ReviewApprovalNotifications :items="reviewNotifications" :error="reviewNotificationsError" :clearing="clearingReviewNotifications" @clear="clearReviewNotifications" @navigate="closeBellMenu" />
              <div v-if="showBirthdayBellSection" class="app-topbar__bell-section">
                <div class="app-topbar__bell-head">
                  <strong>Cumpleaños de hoy</strong>
                </div>
                <div class="app-topbar__bell-list">
                  <article v-for="item in birthdayTodayAlerts" :key="item.person_id" class="app-topbar__bell-item">
                    <div>
                      <p class="app-topbar__bell-title">{{ item.partner_name }}</p>
                      <p class="app-topbar__bell-meta">{{ getBirthdayCompaniesLabel(item) }}</p>
                    </div>
                    <div class="app-topbar__bell-actions">
                      <button v-if="hasPermission('READFORM')" type="button" @click="openBirthdayDetail(item)">Ver cliente</button>
                    </div>
                  </article>
                </div>
              </div>
              <div class="app-topbar__bell-section">
                <div class="app-topbar__bell-head">
                  <strong>Tareas asignadas</strong>
                </div>
                <p v-if="assignedTasksLoading" class="app-topbar__bell-empty" role="status">Actualizando tareas…</p>
                <p v-if="assignedTasksError" class="app-topbar__bell-empty" role="alert">{{ assignedTasksError }}</p>
                <div v-if="assignedTasks.length" class="app-topbar__bell-list">
                  <article v-for="task in assignedTasks" :key="task.id" class="app-topbar__bell-item">
                    <div>
                      <p class="app-topbar__bell-title">{{ task.title }}</p>
                      <p class="app-topbar__bell-meta">{{ task.company_name }} · {{ task.status }}</p>
                    </div>
                    <div class="app-topbar__bell-actions">
                      <button v-if="(!task.task_type || task.task_type === 'GENERAL') && task.status === 'ASIGNADA'" type="button" :disabled="changingTaskId === task.id" @click="updateTaskStatus(task, 'TRABAJANDO')">Trabajando</button>
                      <button v-if="(!task.task_type || task.task_type === 'GENERAL') && task.status === 'TRABAJANDO'" type="button" :disabled="changingTaskId === task.id" @click="updateTaskStatus(task, 'TERMINADA')">Terminada</button>
                      <button type="button" :disabled="changingTaskId === task.id" @click="openTaskDetail(task)">Ver detalle</button>
                    </div>
                  </article>
                </div>
                <p v-else-if="!assignedTasksLoading && !assignedTasksError" class="app-topbar__bell-empty">No tienes tareas asignadas pendientes.</p>
              </div>
            </div>
          </div>
          <div
            class="app-topbar__account navbar-item has-dropdown"
            :class="{ 'is-active': isAccountMenuOpen }"
            @mouseenter="openAccountMenu"
            @mouseleave="scheduleCloseAccountMenu"
            @focusin="openAccountMenu"
            @focusout="handleAccountFocusOut"
          >
            <a class="app-topbar__account-trigger navbar-link" href="#" @click.prevent="toggleAccountMenu">
              <span class="app-topbar__account-icon"><i class="fas fa-user"></i></span>
              <span class="app-topbar__account-copy">
                <span class="app-topbar__account-label">{{ accountLabel }}</span>
                <span class="app-topbar__account-subtitle">Preferencias y acceso</span>
              </span>
            </a>
            <div
              class="app-topbar__dropdown navbar-dropdown is-right"
              @mouseenter="openAccountMenu"
              @mouseleave="scheduleCloseAccountMenu"
            >
              <a class="app-topbar__dropdown-item navbar-item" @click.prevent="showModalFunc()">
                <span class="app-topbar__dropdown-icon"><i class="fas fa-key"></i></span>
                <span>Cambiar contraseña</span>
              </a>
              <a class="app-topbar__dropdown-item navbar-item" @click.prevent="closeSession()">
                <span class="app-topbar__dropdown-icon"><i class="fas fa-sign-out-alt"></i></span>
                <span>Cerrar sesión</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div id="navbarBasicExample" class="app-topbar__menu navbar-menu" :class="{ 'is-active': isBurgerActive }">
        <div class="app-topbar__mobile-links navbar-start">
          <router-link to="/" class="app-topbar__mobile-link is-hidden-desktop is-hidden-widescreen is-hidden-fullhd">
            Clientes
          </router-link>

          <router-link
            v-if="canManageUsers()"
            to="/tracking"
            class="app-topbar__mobile-link is-hidden-desktop is-hidden-widescreen is-hidden-fullhd"
          >
            Seguimiento General
          </router-link>

          <router-link
            v-if="canManageUsers()"
            to="/bulk-imports"
            class="app-topbar__mobile-link is-hidden-desktop is-hidden-widescreen is-hidden-fullhd"
          >
            Cargas masivas
          </router-link>

          <router-link
            v-if="canManageUsers()"
            to="/users"
            class="app-topbar__mobile-link is-hidden-desktop is-hidden-widescreen is-hidden-fullhd"
          >
            Usuarios
          </router-link>

          <router-link
            v-if="hasPermission('menuCONF')"
            to="/settings"
            class="app-topbar__mobile-link is-hidden-desktop is-hidden-widescreen is-hidden-fullhd"
          >
            Configuración
          </router-link>
          <router-link to="/about" class="app-topbar__mobile-link is-hidden-desktop is-hidden-widescreen is-hidden-fullhd">
            Estado operativo
          </router-link>
        </div>

        <div class="app-topbar__actions is-hidden-desktop is-hidden-widescreen is-hidden-fullhd">
          <div class="app-topbar__bell" :class="{ 'is-active': isBellMenuOpen }">
            <a class="app-topbar__bell-trigger" href="#" @click.prevent="toggleBellMenu">
              <span class="app-topbar__bell-icon">
                <i class="fas fa-bell"></i>
              </span>
              <span v-if="totalBellCount" class="app-topbar__bell-count">{{ totalBellCount }}</span>
            </a>
            <div v-if="isBellMenuOpen" class="app-topbar__bell-dropdown">
              <ReviewApprovalNotifications :items="reviewNotifications" :error="reviewNotificationsError" :clearing="clearingReviewNotifications" @clear="clearReviewNotifications" @navigate="closeBellMenu" />
              <div v-if="showBirthdayBellSection" class="app-topbar__bell-section">
                <div class="app-topbar__bell-head">
                  <strong>Cumpleaños de hoy</strong>
                </div>
                <div class="app-topbar__bell-list">
                  <article v-for="item in birthdayTodayAlerts" :key="item.person_id" class="app-topbar__bell-item">
                    <div>
                      <p class="app-topbar__bell-title">{{ item.partner_name }}</p>
                      <p class="app-topbar__bell-meta">{{ getBirthdayCompaniesLabel(item) }}</p>
                    </div>
                    <div class="app-topbar__bell-actions">
                      <button v-if="hasPermission('READFORM')" type="button" @click="openBirthdayDetail(item)">Ver cliente</button>
                    </div>
                  </article>
                </div>
              </div>
              <div class="app-topbar__bell-section">
                <div class="app-topbar__bell-head">
                  <strong>Tareas asignadas</strong>
                </div>
                <p v-if="assignedTasksLoading" class="app-topbar__bell-empty" role="status">Actualizando tareas…</p>
                <p v-if="assignedTasksError" class="app-topbar__bell-empty" role="alert">{{ assignedTasksError }}</p>
                <div v-if="assignedTasks.length" class="app-topbar__bell-list">
                  <article v-for="task in assignedTasks" :key="task.id" class="app-topbar__bell-item">
                    <div>
                      <p class="app-topbar__bell-title">{{ task.title }}</p>
                      <p class="app-topbar__bell-meta">{{ task.company_name }} · {{ task.status }}</p>
                    </div>
                    <div class="app-topbar__bell-actions">
                      <button v-if="(!task.task_type || task.task_type === 'GENERAL') && task.status === 'ASIGNADA'" type="button" :disabled="changingTaskId === task.id" @click="updateTaskStatus(task, 'TRABAJANDO')">Trabajando</button>
                      <button v-if="(!task.task_type || task.task_type === 'GENERAL') && task.status === 'TRABAJANDO'" type="button" :disabled="changingTaskId === task.id" @click="updateTaskStatus(task, 'TERMINADA')">Terminada</button>
                      <button type="button" :disabled="changingTaskId === task.id" @click="openTaskDetail(task)">Ver detalle</button>
                    </div>
                  </article>
                </div>
                <p v-else-if="!assignedTasksLoading && !assignedTasksError" class="app-topbar__bell-empty">No tienes tareas asignadas pendientes.</p>
              </div>
            </div>
          </div>
          <div
            class="app-topbar__account navbar-item has-dropdown"
            :class="{ 'is-active': isAccountMenuOpen }"
            @mouseenter="openAccountMenu"
            @mouseleave="scheduleCloseAccountMenu"
            @focusin="openAccountMenu"
            @focusout="handleAccountFocusOut"
          >
            <a class="app-topbar__account-trigger navbar-link" href="#" @click.prevent="toggleAccountMenu">
              <span class="app-topbar__account-icon"><i class="fas fa-user"></i></span>
              <span class="app-topbar__account-copy">
                <span class="app-topbar__account-label">{{ accountLabel }}</span>
                <span class="app-topbar__account-subtitle">Preferencias y acceso</span>
              </span>
            </a>
            <div
              class="app-topbar__dropdown navbar-dropdown is-right"
              @mouseenter="openAccountMenu"
              @mouseleave="scheduleCloseAccountMenu"
            >
              <a class="app-topbar__dropdown-item navbar-item" @click.prevent="showModalFunc()">
                <span class="app-topbar__dropdown-icon"><i class="fas fa-key"></i></span>
                <span>Cambiar contraseña</span>
              </a>
              <a class="app-topbar__dropdown-item navbar-item" @click.prevent="closeSession()">
                <span class="app-topbar__dropdown-icon"><i class="fas fa-sign-out-alt"></i></span>
                <span>Cerrar sesión</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <section>
      <div class="modal premium-modal" id="modalChangePassword">
        <div class="modal-background premium-modal__backdrop" @click="closeModalFunc()"></div>
        <div class="modal-card premium-modal__card premium-modal__card--password">
          <header class="modal-card-head premium-modal__head">
            <div class="premium-modal__heading">
              <p class="premium-modal__eyebrow">Seguridad de cuenta</p>
              <p class="modal-card-title premium-modal__title">Cambiar Contraseña</p>
            </div>
            <button class="delete premium-modal__close" @click.prevent="closeModalFunc()" aria-label="close"></button>
          </header>
          <section class="modal-card-body premium-modal__body premium-modal__body--password">
            <changePassword />
          </section>
          <footer class="modal-card-foot premium-modal__foot">
            <button class="button premium-modal__action premium-modal__action--secondary" @click.prevent="closeModalFunc()">
              Cerrar
            </button>
          </footer>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import auth from '@/auth/index'
import branding from '@/config/branding'
import changePassword from '@/components/changePassword.vue'
import ChangePassword from './changePassword.vue'
import trackingService from '@/services/tracking.service'
import ReviewApprovalNotifications from '@/components/ReviewApprovalNotifications.vue'
export default {
  components: { changePassword, ChangePassword, ReviewApprovalNotifications },
  data: () => ({
    showFormModal: true,
    error: '',
    strError: '',
    branding,
    isBurgerActive: false,
    isAccountMenuOpen: false,
    accountMenuCloseTimer: null,
    isBellMenuOpen: false,
    assignedTasks: [],
    reviewNotifications: [],
    reviewNotificationsError: '',
    clearingReviewNotifications: false,
    reviewNotificationsRequestId: 0,
    reviewNotificationsSeenId: 0,
    bellRefreshTimer: null,
    bellLoading: false,
    assignedTasksLoading: false,
    assignedTasksError: '',
    assignedTasksCount: 0,
    birthdayTodayAlerts: [],
    changingTaskId: null
  }),
  computed: {
    accountLabel () {
      const userLogged = auth.getUserLogged() || {}
      return userLogged.email || 'Cuenta'
    },
    totalBellCount () {
      return this.assignedTasksCount + this.birthdayTodayAlerts.length + this.unseenReviewCount
    },
    unseenReviewCount () {
      return this.reviewNotifications.filter(item => item.id > this.reviewNotificationsSeenId).length
    },
    showBirthdayBellSection () {
      return auth.canViewBirthdays() && this.birthdayTodayAlerts.length > 0
    }
  },
  watch: {
    '$route.fullPath' () { this.refreshVisibleNotifications() }
  },
  beforeDestroy () {
    window.removeEventListener('company-tasks-updated', this.loadBellNotifications)
    window.removeEventListener('focus', this.refreshVisibleNotifications)
    document.removeEventListener('visibilitychange', this.refreshVisibleNotifications)
    window.clearInterval(this.bellRefreshTimer)
    this.clearAccountMenuCloseTimer()
  },
  mounted () {
    this.readSeenReviewNotifications()
    window.addEventListener('company-tasks-updated', this.loadBellNotifications)
    window.addEventListener('focus', this.refreshVisibleNotifications)
    document.addEventListener('visibilitychange', this.refreshVisibleNotifications)
    this.bellRefreshTimer = window.setInterval(this.refreshVisibleNotifications, 15000)
    this.loadBellNotifications()
  },
  methods: {
    reviewSeenStorageKey () {
      const user = auth.getUserLogged() || {}
      return `odin:review-notifications-seen:${user.id || user.email || 'anonymous'}`
    },
    readSeenReviewNotifications () {
      try {
        this.reviewNotificationsSeenId = Number(window.localStorage.getItem(this.reviewSeenStorageKey())) || 0
      } catch (error) {
        this.reviewNotificationsSeenId = 0
      }
    },
    markReviewNotificationsSeen () {
      this.reviewNotificationsSeenId = Math.max(this.reviewNotificationsSeenId, ...this.reviewNotifications.map(item => item.id))
      try {
        window.localStorage.setItem(this.reviewSeenStorageKey(), String(this.reviewNotificationsSeenId))
      } catch (error) {
        // Keep the read state in memory if browser storage is unavailable.
      }
    },
    refreshVisibleNotifications () {
      if (document.visibilityState === 'hidden' || !auth.isLogged()) return
      this.loadBellNotifications()
    },
    hasPermission (code) {
      return auth.hasUserPermission(code)
    },
    canManageUsers () {
      return auth.canManageUsers()
    },
    async loadBellNotifications () {
      if (this.bellLoading || !auth.isLogged()) return
      this.bellLoading = true
      try {
        await Promise.all([
          this.loadAssignedTasks(),
          this.loadReviewNotifications(),
          this.loadBirthdayTodayAlerts()
        ])
      } finally {
        this.bellLoading = false
      }
    },
    async clearReviewNotifications () {
      if (this.clearingReviewNotifications || !this.reviewNotifications.length) return
      const throughId = Math.max(...this.reviewNotifications.map(item => item.id))
      this.clearingReviewNotifications = true
      this.reviewNotificationsRequestId++
      this.reviewNotificationsError = ''
      try {
        await this.$axios.post('company/review-notifications/clear', { through_id: throughId })
        this.reviewNotifications = this.reviewNotifications.filter(item => item.id > throughId)
      } catch (error) {
        this.reviewNotificationsError = 'No fue posible limpiar las notificaciones. Inténtalo nuevamente.'
      } finally {
        this.clearingReviewNotifications = false
      }
    },
    async loadReviewNotifications () {
      if (this.clearingReviewNotifications) return
      const requestId = ++this.reviewNotificationsRequestId
      this.reviewNotificationsError = ''
      try {
        const response = await this.$axios.get('company/review-notifications')
        if (requestId !== this.reviewNotificationsRequestId) return
        this.reviewNotifications = response.data.notifications || []
        if (this.isBellMenuOpen && document.visibilityState !== 'hidden') this.markReviewNotificationsSeen()
      } catch (error) {
        if (requestId !== this.reviewNotificationsRequestId) return
        this.reviewNotificationsError = 'No fue posible cargar las aprobaciones. Abre nuevamente la campana para reintentar.'
      }
    },
    async loadAssignedTasks () {
      if (this.assignedTasksLoading) return
      this.assignedTasksLoading = true
      this.assignedTasksError = ''
      try {
        const tasks = await trackingService.getMyAssignedTasks()
        this.assignedTasks = tasks
        this.assignedTasksCount = tasks.length
      } catch (error) {
        const status = error.response && error.response.status
        this.assignedTasksError = status === 401 || status === 422
          ? 'No fue posible validar tu sesión. Vuelve a iniciar sesión para consultar las tareas.'
          : error.response
            ? 'No fue posible cargar las tareas asignadas. Abre nuevamente la campana para reintentar.'
            : error.message || 'No fue posible conectar con el servidor de tareas.'
      } finally {
        this.assignedTasksLoading = false
      }
    },
    async loadBirthdayTodayAlerts () {
      if (!auth.canViewBirthdays()) {
        this.birthdayTodayAlerts = []
        return
      }
      try {
        const response = await this.$axios.get('company/getUpcomingPartnerBirthdays', {
          params: {
            daysAhead: 7
          }
        })
        const items = response.data.items || []
        this.birthdayTodayAlerts = items.filter(item => item.is_today)
      } catch (error) {
        this.birthdayTodayAlerts = []
      }
    },
    clearAccountMenuCloseTimer () {
      if (this.accountMenuCloseTimer) {
        window.clearTimeout(this.accountMenuCloseTimer)
        this.accountMenuCloseTimer = null
      }
    },
    openAccountMenu () {
      this.clearAccountMenuCloseTimer()
      this.isAccountMenuOpen = true
    },
    closeAccountMenu () {
      this.clearAccountMenuCloseTimer()
      this.isAccountMenuOpen = false
    },
    scheduleCloseAccountMenu () {
      this.clearAccountMenuCloseTimer()
      this.accountMenuCloseTimer = window.setTimeout(() => {
        this.isAccountMenuOpen = false
        this.accountMenuCloseTimer = null
      }, 140)
    },
    toggleAccountMenu () {
      if (this.isAccountMenuOpen) {
        this.closeAccountMenu()
        return
      }
      this.openAccountMenu()
    },
    handleAccountFocusOut (event) {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return
      }
      this.closeAccountMenu()
    },
    toggleBellMenu () {
      this.isBellMenuOpen = !this.isBellMenuOpen
      if (this.isBellMenuOpen) {
        this.markReviewNotificationsSeen()
        this.loadBellNotifications()
      }
    },
    closeBellMenu () {
      this.isBellMenuOpen = false
    },
    async updateTaskStatus (task, status) {
      this.changingTaskId = task.id
      try {
        await trackingService.changeCompanyTaskStatus({ id: task.id, status })
        await this.loadAssignedTasks()
      } catch (error) {
        console.log(error)
      } finally {
        this.changingTaskId = null
      }
    },
    openTaskDetail (task) {
      this.closeBellMenu()
      this.$router.push({
        name: 'CompanyShow',
        params: { id: String(task.company_id) },
        query: { tab: 'tracking', task: String(task.id) }
      }).catch(() => {})
    },
    getBirthdayCompaniesLabel (item) {
      if (!item || !item.companies || !item.companies.length) return 'Sin clientes asociados'
      if (item.companies.length === 1) return item.companies[0].company_name
      const [firstCompany, ...remainingCompanies] = item.companies
      return `${firstCompany.company_name} y ${remainingCompanies.length} cliente(s) mas`
    },
    openBirthdayDetail (item) {
      if (!this.hasPermission('READFORM')) return
      if (!item || !item.companies || !item.companies.length) return
      this.closeBellMenu()
      this.$router.push({
        name: 'CompanyShow',
        params: { id: String(item.companies[0].company_id) }
      }).catch(() => {})
    },
    closeSession () {
      this.closeAccountMenu()
      this.$axios
        .post('logout')
        .then(response => {
          const result = response.data
          console.log(result)
          auth.closeSession()
          this.$router.push({ name: 'LoginView' })
        })
        .catch(error => {
          console.log(error)
          auth.closeSession()
          this.$router.push({ name: 'LoginView' })
        })
    },
    changePassword2 () {

    },
    burgerClick () {
      this.isBurgerActive = !this.isBurgerActive
    },
    showModalFunc () {
      this.closeAccountMenu()
      this.showFormModal = true
      var Modal = document.querySelector('#modalChangePassword')
      Modal.classList.toggle('is-active')
    },
    closeModalFunc () {
      var Modal = document.querySelector('#modalChangePassword')
      Modal.classList.toggle('is-active')
      this.showFormModal = false
    }
  }
}
</script>

<style scoped>
.app-topbar {
  position: relative;
  z-index: 200;
  overflow: visible;
  isolation: isolate;
  margin: 0.75rem 0.75rem 0;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.48);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.9), rgba(244, 238, 229, 0.82));
  box-shadow: 0 24px 60px rgba(43, 34, 24, 0.07);
  backdrop-filter: blur(16px);
}

.app-topbar__brand-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  overflow: visible;
}

.app-topbar__brand {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.app-topbar__brand-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0.55rem 0.8rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.62);
}

.app-topbar__brand-logo {
  display: block;
  width: 96px;
  height: auto;
}

.app-topbar__brand-logo--client {
  width: auto;
  max-width: 132px;
  max-height: 34px;
  object-fit: contain;
}

.app-topbar__brand-copy {
  text-align: left;
}

.app-topbar__eyebrow {
  margin: 0;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.22rem;
  color: rgba(36, 31, 25, 0.46);
}

.app-topbar__title {
  margin: 0.22rem 0 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: #211c17;
}

.app-topbar__subtitle {
  margin: 0.14rem 0 0;
  font-size: 0.76rem;
  color: rgba(36, 31, 25, 0.62);
}

.app-topbar__burger {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.6);
  color: #211c17;
}

.app-topbar__burger:hover,
.app-topbar__burger:focus {
  background: rgba(255, 255, 255, 0.82);
}

.app-topbar__menu {
  position: relative;
  z-index: 2;
  overflow: visible;
  background: transparent;
  box-shadow: none;
}

.app-topbar__actions {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 0.6rem;
  overflow: visible;
}

.app-topbar__actions--desktop {
  margin-left: auto;
}

.app-topbar__bell {
  position: relative;
}

.app-topbar__bell-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.64);
  color: #211c17;
}

.app-topbar__bell-icon {
  font-size: 1rem;
}

.app-topbar__bell-count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #be3f3f;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.app-topbar__bell-dropdown {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  width: min(420px, calc(100vw - 2rem));
  padding: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 18px;
  background: rgba(255, 252, 247, 0.98);
  box-shadow: 0 22px 46px rgba(43, 34, 24, 0.12);
  z-index: 550;
}

.app-topbar__bell-head {
  margin-bottom: 0.55rem;
  color: #211c17;
}

.app-topbar__bell-section + .app-topbar__bell-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(143, 117, 68, 0.12);
}

.app-topbar__bell-list {
  display: grid;
  gap: 0.55rem;
}

.app-topbar__bell-item {
  display: grid;
  gap: 0.5rem;
  padding: 0.7rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.64);
}

.app-topbar__bell-title,
.app-topbar__bell-meta,
.app-topbar__bell-empty {
  margin: 0;
}

.app-topbar__bell-title {
  color: #211c17;
  font-weight: 700;
}

.app-topbar__bell-meta,
.app-topbar__bell-empty {
  color: rgba(36, 31, 25, 0.58);
  font-size: 0.84rem;
}

.app-topbar__bell-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.app-topbar__bell-actions button {
  min-height: 2rem;
  padding: 0 0.7rem;
  border: 1px solid rgba(102, 81, 57, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #352b21;
}

.app-topbar__account {
  position: relative;
  z-index: 3;
  margin-left: auto;
  padding-bottom: 0.55rem;
  overflow: visible;
}

.app-topbar__account-trigger {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 46px;
  padding: 0.35rem 0.55rem 0.35rem 0.4rem;
  border-radius: 16px;
  color: #211c17;
}

.app-topbar__account-trigger:hover,
.app-topbar__account-trigger:focus {
  background: rgba(255, 255, 255, 0.56);
  color: #211c17;
}

.app-topbar__account-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: linear-gradient(135deg, #28211b 0%, #4a3c2b 100%);
  color: #f7f0e5;
  box-shadow: 0 10px 20px rgba(54, 42, 28, 0.16);
}

.app-topbar__account-copy {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.app-topbar__account-label {
  font-size: 0.88rem;
  font-weight: 700;
  color: #211c17;
}

.app-topbar__account-subtitle {
  font-size: 0.75rem;
  color: rgba(36, 31, 25, 0.54);
}

.app-topbar__dropdown {
  position: absolute;
  top: calc(100% - 0.1rem);
  left: auto;
  right: 0;
  min-width: 240px;
  margin-top: 0;
  padding: 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 18px;
  background: rgba(255, 252, 247, 0.98);
  box-shadow: 0 22px 46px rgba(43, 34, 24, 0.12);
  z-index: 500;
}

.app-topbar__dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-height: 44px;
  border-radius: 14px;
  color: #211c17;
}

.app-topbar__dropdown-item:hover,
.app-topbar__dropdown-item:focus {
  background: rgba(245, 238, 226, 0.9);
  color: #211c17;
}

.app-topbar__dropdown-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.72);
}

.app-topbar__mobile-links {
  padding-top: 0.65rem;
}

.app-topbar__mobile-link {
  display: block;
  padding: 0.8rem 0.2rem;
  color: #211c17;
  font-weight: 600;
}

.premium-modal {
  z-index: 700;
  padding: 1rem;
}

.premium-modal__backdrop {
  background: rgba(22, 18, 14, 0.58);
  backdrop-filter: blur(8px);
}

.premium-modal__card {
  width: min(720px, calc(100vw - 2rem));
  max-height: none;
  border: 1px solid rgba(255, 255, 255, 0.48);
  border-radius: 30px;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(234, 220, 202, 0.55), transparent 32%),
    linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(248, 242, 234, 0.95));
  box-shadow: 0 34px 90px rgba(33, 24, 16, 0.24);
}

.premium-modal__card--password {
  width: min(640px, calc(100vw - 2rem));
}

.premium-modal__head,
.premium-modal__body,
.premium-modal__foot {
  border: 0;
  background: transparent;
}

.premium-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.7rem 1.7rem 1.15rem;
}

.premium-modal__heading {
  min-width: 0;
}

.premium-modal__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(68, 55, 41, 0.55);
}

.premium-modal__title {
  margin: 0;
  color: #201a14;
  font-size: clamp(1.9rem, 3vw, 2.35rem);
  font-weight: 650;
  letter-spacing: -0.04em;
  line-height: 1;
}

.premium-modal__close {
  flex-shrink: 0;
  margin-left: auto;
  width: 2rem;
  height: 2rem;
  background: rgba(255, 255, 255, 0.54);
  border: 1px solid rgba(88, 71, 53, 0.08);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 8px 20px rgba(56, 42, 28, 0.08);
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.premium-modal__close:hover,
.premium-modal__close:focus {
  background: rgba(255, 255, 255, 0.78);
  transform: translateY(-1px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.94),
    0 12px 24px rgba(56, 42, 28, 0.12);
}

.premium-modal__body {
  padding: 0.35rem 1.7rem 0;
  overflow: visible;
}

.premium-modal__body--password {
  padding-bottom: 0.4rem;
}

.premium-modal__foot {
  display: flex;
  justify-content: flex-start;
  gap: 0.75rem;
  padding: 1.35rem 1.7rem 1.7rem;
}

.premium-modal__action {
  height: 3rem;
  padding: 0 1.15rem;
  border-radius: 999px;
  font-weight: 600;
  box-shadow: none;
}

.premium-modal__action--secondary {
  min-width: 7.35rem;
  border: 1px solid rgba(69, 55, 40, 0.14);
  background: rgba(255, 255, 255, 0.86);
  color: #4a3a2c;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.96),
    0 8px 18px rgba(66, 48, 29, 0.05);
}

.premium-modal__action--secondary:hover,
.premium-modal__action--secondary:focus {
  border-color: rgba(69, 55, 40, 0.22);
  background: rgba(255, 255, 255, 0.96);
  color: #2f2419;
}

@media (max-width: 640px) {
  .premium-modal {
    padding: 0.75rem;
  }

  .premium-modal__card,
  .premium-modal__card--password {
    width: calc(100vw - 1.5rem);
    border-radius: 24px;
  }

  .premium-modal__head,
  .premium-modal__body,
  .premium-modal__foot {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .premium-modal__title {
    font-size: 1.6rem;
  }

  .premium-modal__foot {
    flex-direction: column;
  }

  .premium-modal__action {
    width: 100%;
  }

  .app-topbar__burger {
    display: inline-flex;
  }

  .app-topbar__menu {
    display: none;
  }

  .app-topbar__menu.is-active {
    display: block;
    padding-top: 0.7rem;
  }

  .app-topbar__actions {
    padding-top: 0.35rem;
  }

  .app-topbar__account {
    width: 100%;
    padding-bottom: 0;
  }

  .app-topbar__bell {
    width: 100%;
  }

  .app-topbar__bell-trigger {
    width: 100%;
    justify-content: center;
  }

  .app-topbar__account-trigger {
    width: 100%;
    justify-content: space-between;
  }

  .app-topbar__dropdown {
    position: static;
    min-width: 0;
    margin-top: 0.45rem;
  }

  .app-topbar__bell-dropdown {
    position: static;
    width: 100%;
    margin-top: 0.45rem;
  }
}

@media (max-width: 1023px) {
  .app-topbar__menu {
    width: 100%;
    padding-top: 0.5rem;
  }

  .app-topbar__actions {
    justify-content: flex-start;
    padding-top: 0.45rem;
  }

  .app-topbar__account {
    margin-left: 0;
  }

  .app-topbar__brand-copy {
    display: none;
  }
}
</style>
